import { setBulkUploadDtl } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useCommitBulkUploadUserMutation } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "@/components/Toast";

const useStep3 = ({onNextStepClick}: {onNextStepClick: any}) => {
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const bulkUploadDtl = useSelector(
    (state: RootState) => state.currentData.bulkUploadDtl
  );
  const dispatch = useDispatch();
  const [openTabs, setOpenTabs] = useState(["1"]);
  const [updatedRows, setUpdatedRows] = useState([]);

  useEffect(() => {
    let rows = bulkUploadDtl?.rows?.filter((data: any) => data?.status?.toLowerCase() == "duplicate");
    rows = rows?.map((data: any) => ({
      ...data,
      matchCandidates: data?.matchCandidates?.map((data: any, index: number) => ({...data, isSelected: index == 0}))
    }))
    setUpdatedRows(rows);
  }, [bulkUploadDtl])
  

  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };

  const columns = [
    {
      accessorKey: "field",
      header: <div>Field</div>,
      accessorFn: (row: any) => String(row?.field || ""),
    },
    {
      accessorKey: "fromYourFile",
      header: <div>From Your File</div>,
      accessorFn: (row: any) => String(row?.fromYourFile || ""),
    },
    {
      accessorKey: "matchCandid",
      header: <div>Match Candidate</div>,
      accessorFn: (row: any) => String(row?.matchCandid || ""),
    },
  ]

  const tableData = useMemo(() => {
    const selectedRow: any = updatedRows[Number(openTabs?.[0]) - 1];
    const selectedCard: any = (selectedRow as any)?.matchCandidates?.find((data: any) => data?.isSelected); 
    const propertyLinksMatchCandid = selectedCard?.propertyLinks?.map((data: any) => data?.propertyName)?.join(", ");
    return [
      {
        field: "Name",
        fromYourFile: selectedRow?.owner,
        matchCandid: selectedCard?.name,
        isSame: selectedRow?.owner == selectedCard?.name
      },
      {
        field: "Mobile",
        fromYourFile: selectedRow?.mobileNumber,
        matchCandid: selectedCard?.mobileNumber,
        isSame: selectedRow?.mobileNumber == selectedCard?.mobileNumber
      },
      {
        field: "Email",
        fromYourFile: selectedRow?.email,
        matchCandid: selectedCard?.email,
        isSame: selectedRow?.email == selectedCard?.email
      },
      {
        field: "Property Link",
        fromYourFile: selectedRow?.propertyName,
        matchCandid: propertyLinksMatchCandid,
        isSame: selectedRow?.propertyName == propertyLinksMatchCandid
      },
      {
        field: "Address",
        fromYourFile: selectedRow?.address,
        matchCandid: selectedCard?.address,
        isSame: selectedRow?.address == selectedCard?.address
      }
    ]
  }, [updatedRows]);

  const onChangeCandid = (userId: any) => {
    let rows: any = updatedRows?.map((data: any) => ({
      ...data,
      matchCandidates: data?.matchCandidates?.map((data: any) => ({...data, isSelected: data?.userId == userId}))
    }))
    setUpdatedRows(rows);
  }

  const { mutate: commitUpload, isPending: isUploadPending } =
    useCommitBulkUploadUserMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        dispatch(setBulkUploadDtl({...bulkUploadDtl, commitData: {...data?.data?.data} }))
        onNextStepClick?.();
      },
  });

  const onImport = () => {
    commitUpload({
      communityId: communityData?.communityId,
      attachmentId: bulkUploadDtl?.attachmentId,
      data: {
        "includeWarnings": true,
        duplicateResolutions: updatedRows?.map((data: any) => ({
          row: data?.row,
          action: data?.action == "discard" ? "DISCARD" : "MERGE",
          ...(data?.action == "merged" ? {selectedUserId: data?.matchCandidates?.find((data: any) => data?.isSelected)?.userId} : {})
        }))
      }
    })
  }

  const onDiscard = (index: number) => (e: any) => {
    e.preventDefault();
    let rows: any = updatedRows?.map((data: any, idx: number) => ({
      ...data,
      action: index == idx ? "discard" : data?.action
    }))
    setUpdatedRows(rows);
  }

  const onMerge = (index: number) => (e: any) => {
    e.preventDefault();
    let rows: any = updatedRows?.map((data: any, idx: number) => ({
      ...data,
      action: index == idx ? "merged" : data?.action
    }))
    setUpdatedRows(rows);
  }

  const onMergeAll = (e: any) => {
    e.preventDefault();
    let rows: any = updatedRows?.map((data: any) => ({
      ...data,
      action: "merged"
    }))
    setUpdatedRows(rows);
  }

  const onDiscardAll = (e: any) => {
    e.preventDefault();
    let rows: any = updatedRows?.map((data: any) => ({
      ...data,
      action: "discard"
    }))
    setUpdatedRows(rows);
  }
  
  const disabledButton = useMemo(() => updatedRows?.every((data: any) => data?.action), [updatedRows]);

  const allActionUpdated = useMemo(() => {
    return updatedRows?.every((data: any) => data.action);
  }, [updatedRows]);

  return {
    communityData,
    updatedRows,
    openTabs,
    onAccordionChange,
    columns,
    onDiscard,
    tableData,
    onChangeCandid,
    isUploadPending,
    onImport,
    onMerge,
    disabledButton,
    onMergeAll,
    onDiscardAll,
    allActionUpdated
  }
}

export default useStep3;